import { calcGiftAssistFee } from "@qgiv/core-donor";
import { calcStoreTaxes, findProductFromProductId } from "./helpers";
import { constants } from "@qgiv/core-js";

// -------------------------------------------------------------------------
// NOTE: This function has changed a bit. It is no longer as strict and
// complicated with its return.  It is now being used to calculate
// all of the totals found in the summary and returning them as an object.
// If new values need to be calculated and returned feel free to add them.
// Since we are now only calling this function once in the
// UpdateRegistrationTotals component, new values (if they fit within the scope)
// may also need to be added to the registration.totals redux store. All params
// are necessary to run this function which is why we would want to limit it's use.
// -------------------------------------------------------------------------

/**
 * @public
 * @function calcRegistrationTotal
 * @param {object} settings Settings object
 * @param {object} [settings.discounts] Object with Promo Code data
 * @param {Array} [settings.registrations] Array of registrations form data objects
 * @param {Array} [settings.categories] Array of categories as objects
 * @param {number} [settings.optionalDonation] Optional donation amount
 * @param {boolean} [settings.applyFeeCoverage] Flag to apply gift assist
 * @param {boolean} [settings.feeCoverage] Fee coverage setting from form settings
 * @param {number} [settings.feeCoverageFlat] Fee coverage flat setting from form settings
 * @param {number} [settings.feeCoveragePercentage] Fee coverage percentage from form settings
 * @param {Array} [settings.storeCart] Array of Store items in cart
 * @param {object} [settings.products] Object with all products available
 * @param {number} [settings.taxRate] Number representing the store tax percentage
 * @param {number} [settings.shippingMethodAmount] Number representing the store shipping method selected
 * @returns {object} Registration total values. First item is registration total. Second value is
 *                  fee coverage total.
 */
export const calcRegistrationTotal = ({
    registrations = [],
    categories = [],
    discounts = {},
    optionalDonation = 0.0,
    applyFeeCoverage = false,
    feeCoverage = false,
    feeCoverageFlat = 0,
    feeCoveragePercentage = 3,
    storeCart = [],
    products = {},
    taxRate = 0,
    shippingMethodAmount = 0,
}) => {
    const { participants = [], transaction = {} } = discounts;
    // Calculate totals from registrations, includes adjustments for earlyRegistration and
    // promo code discounts
    const registrantSubtotalNumber =
        registrations.length > 0 && registrations[0].Category
            ? registrations
                  // Get an array of registrations that have a category selected
                  .filter(({ Category = [] }) => Category.length > 0)
                  // Get an array of registration fees
                  .map(({ Category }, index) => {
                      const { earlyRegistration, fee, registrationFee } =
                          categories.find((category) => {
                              return category.id === Number(Category[0]);
                          });

                      //  Get participants that correspond with ^^ registration
                      //  Participant needs to be an object, either empty or with the found index
                      //  Can not be undefined or Adding Another participant will break
                      const participant =
                          participants &&
                          participants.length > 0 &&
                          participants.find(({ id }) => id === index)
                              ? participants.find(({ id }) => id === index)
                              : {};

                      // Check if this registration have a promo applied
                      const hasPromo =
                          Object.keys(participant).includes("promoCodes") &&
                          participant.promoCodes.length > 0;

                      // Get the promo discounted price
                      const promoDiscountedFee =
                          participant.postPromoCategoryFee;

                      //   If registrant has promo applied we want to use that price
                      return hasPromo
                          ? promoDiscountedFee
                          : earlyRegistration
                            ? fee
                            : registrationFee;
                  })
                  // Add all registration fees together
                  .reduce((total, registrationFee) => {
                      return (total += registrationFee);
                  }, 0.0)
            : 0.0;
    //  Get data to determine registration as a whole have a promo discount
    const {
        isDiscounted = false,
        amount,
        originalCategoryFeeSum,
    } = transaction;

    // Calculated value for all store items in the cart. Excludes shipping and tax.
    const storeItemsTotal =
        storeCart.length > 0 && products
            ? storeCart
                  .map(
                      ({
                          Product_Id,
                          Product_Quantity,
                          Field_Data_Id = "",
                      }) => {
                          const currentProduct = findProductFromProductId(
                              Product_Id,
                              products,
                          );
                          const {
                              fieldData = {},
                              hasAttributes = false,
                              price_fixed = undefined,
                          } = currentProduct;
                          // Price is stored in two spots, if there are attributes and !price_fixed
                          // the FieldData holds the correct price otherwise cost/price_fixed will be the correct price
                          const currentProductCost =
                              hasAttributes && !price_fixed
                                  ? fieldData[Field_Data_Id].cost
                                  : price_fixed;
                          const productCostByQuantity =
                              Number(currentProductCost) *
                              Number(Product_Quantity);

                          return productCostByQuantity;
                      },
                  )
                  // Add all store items together
                  .reduce((total, productCostByQuantity) => {
                      return (total += productCostByQuantity);
                  }, 0.0)
            : 0.0;

    const storeTaxNumber =
        taxRate && taxRate > 0
            ? Number(
                  calcStoreTaxes(
                      storeItemsTotal,
                      shippingMethodAmount,
                      taxRate,
                  ),
              )
            : 0.0;

    const storeSubTotalNumber =
        storeItemsTotal + storeTaxNumber + shippingMethodAmount;

    const baseTotal =
        (isDiscounted ? amount : registrantSubtotalNumber) + optionalDonation;

    const feeCoverageAmountNumber =
        applyFeeCoverage && feeCoverage
            ? Number(
                  calcGiftAssistFee(baseTotal, {
                      feeCoverage,
                      feeCoverageFlat,
                      feeCoveragePercentage,
                  }),
              )
            : 0.0;

    const giftsSubtotalNumber = optionalDonation + feeCoverageAmountNumber;
    // Total including everything on summary
    const totalNumber =
        baseTotal + feeCoverageAmountNumber + storeSubTotalNumber;

    // For displaying original amount with strike-through if promos were applied only
    // Transaction level
    const prePromoTransTotalNumber =
        originalCategoryFeeSum && originalCategoryFeeSum > 0
            ? originalCategoryFeeSum +
              optionalDonation +
              feeCoverageAmountNumber +
              storeSubTotalNumber
            : totalNumber;
    // Registrant Level
    const prePromoRegistrantNumber =
        originalCategoryFeeSum > 0
            ? originalCategoryFeeSum
            : registrantSubtotalNumber;

    return {
        totalNumber,
        feeCoverageAmountNumber,
        giftsSubtotalNumber,
        prePromoTransTotalNumber,
        prePromoRegistrantNumber,
        storeTaxNumber,
        storeSubTotalNumber,
        registrantSubtotalNumber,
    };
};

/**
 * @public
 * @function determineRegRolesFromSelectedCategoryId
 * @param {object} settings Settings object
 * @param {number} [settings.selectedCategoryId] User selected category id
 * @param {Array} [settings.categories] Array of categories as objects
 * @param {boolean} [settings.enableTeams] Event Level Teams setting
 * @param {boolean} [settings.enableIndividualRegistration] Event Level In
 * @returns {object} Booleans for determining how Category selection should interact with Registration Roles
 */
export const determineRegRolesFromSelectedCategoryId = ({
    selectedCategoryId,
    categories,
    enableTeams,
    enableIndividualRegistration,
}) => {
    let isRegRoleIndividualOnly = false;
    let isRegRoleTeamOnly = false;
    let isRegRoleTeamAndIndividual = false;

    // Return early if you are missing data
    if (categories?.length === 0 || !selectedCategoryId)
        return {
            isRegRoleIndividualOnly,
            isRegRoleTeamOnly,
            isRegRoleTeamAndIndividual,
        };

    const selectedCategoryData =
        categories && selectedCategoryId
            ? categories?.find((category) => {
                  return Number(selectedCategoryId) === category?.id;
              })
            : {};

    const teamRoleAvailable =
        selectedCategoryData?.attachTeamCaptains ||
        selectedCategoryData?.attachTeamMembers;

    const individualRoleAvailable = selectedCategoryData?.attachIndividual;

    if (Object.keys(selectedCategoryData).length > 0) {
        isRegRoleIndividualOnly =
            enableIndividualRegistration &&
            individualRoleAvailable &&
            (!teamRoleAvailable || (!enableTeams && teamRoleAvailable));
        isRegRoleTeamOnly =
            enableTeams &&
            teamRoleAvailable &&
            (!individualRoleAvailable ||
                (!enableIndividualRegistration && individualRoleAvailable));
        isRegRoleTeamAndIndividual =
            !!(enableIndividualRegistration && enableTeams) &&
            individualRoleAvailable &&
            teamRoleAvailable;
    }

    return {
        isRegRoleIndividualOnly,
        isRegRoleTeamOnly,
        isRegRoleTeamAndIndividual,
    };
};

/**
 * @public
 * @function getRoles
 * @param {object} settings Settings object
 * @param {object} [settings.category] Category we are checking for available roles
 * @param {boolean} [settings.enableIndividualRegistration] Event Setting to allow individual registrations
 * @param {boolean} [settings.enableTeams] Event Setting to allow team registrations
 * @returns {Array} Array of objects with Role data to use in Formik Fields
 */
export const getRoles = ({
    category,
    enableIndividualRegistration,
    enableTeams,
}) => {
    const { ENUMS } = constants;
    const {
        EntityType: { REGISTRATION, TEAM },
    } = ENUMS;
    const { attachIndividual, attachTeamCaptains, attachTeamMembers } =
        category;
    const individual = {
        entityType: REGISTRATION,
        id: "Registration-Role-Individual",
        value: `${REGISTRATION}`,
    };
    const team = {
        entityType: TEAM,
        id: "Registration-Role-Team",
        value: `${TEAM}`,
    };
    let roles = [];
    if (enableIndividualRegistration && attachIndividual) {
        roles = [individual];
    }
    if (enableTeams && !!(attachTeamCaptains || attachTeamMembers)) {
        roles = [...roles, team];
    }

    return roles;
};

/**
 * @public
 * @function getShouldSetRemainingFormToShow
 * @param {object} _newInitialValues Settings object
 * @returns {boolean} Flag used to set the remaining form to show based on new initialValues
 */
export const getShouldSetRemainingFormToShow = (_newInitialValues) => {
    const { ENUMS } = constants;
    const {
        EntityType: { REGISTRATION, TEAM },
    } = ENUMS;

    let shouldSetRemainingFormToShow = false;

    const hasCategorySet = _newInitialValues?.Category[0]?.length > 0;
    const hasIndividualRoleSet =
        _newInitialValues?.Registration_Role[0] === `${REGISTRATION}`;
    const hasFullTeamRoleSet =
        _newInitialValues?.Registration_Role[0] === `${TEAM}` &&
        _newInitialValues?.Team?.length > 0;

    if (
        Object.keys(_newInitialValues).length > 0 &&
        hasCategorySet &&
        !!(hasIndividualRoleSet || hasFullTeamRoleSet)
    ) {
        shouldSetRemainingFormToShow = true;
    }
    return shouldSetRemainingFormToShow;
};
